import {
  DEFAULT_SETTINGS_PLUGIN_POLICY,
  normalizeSettingsPluginPolicy,
  SETTINGS_PLUGIN_PANEL_IDS
} from '../../../../packages/plugin-sdk/src/settings-plugin-policy.js';

export const SETTINGS_APPEARANCE_PANEL_ID = 'appearance';
export const SETTINGS_ADMIN_PANEL_IDS = Object.freeze(
  SETTINGS_PLUGIN_PANEL_IDS.filter((panelId) => panelId !== SETTINGS_APPEARANCE_PANEL_ID)
);

export function getNormalizedSettingsPluginPolicy(policy) {
  return normalizeSettingsPluginPolicy(policy, []);
}

function hasContributionCapability(access, capability = '') {
  const wanted = String(capability || '').trim();
  if (!wanted) return true;
  const list = Array.isArray(access && access.list) ? access.list : [];
  if (list.length === 0) return false;
  if (list.includes('*') || list.includes(wanted)) return true;
  const parts = wanted.split('.');
  while (parts.length > 1) {
    parts.pop();
    if (list.includes(`${parts.join('.')}.*`)) return true;
  }
  return false;
}

function filterContributionDescriptors(descriptors, access) {
  const values = Array.isArray(descriptors) ? descriptors : [];
  return values.filter((descriptor) => (
    descriptor
    && typeof descriptor === 'object'
    && hasContributionCapability(access, descriptor.requiredCapability)
  ));
}

export function resolveSettingsViewPolicy({
  role,
  authMode,
  canRead,
  canAdmin,
  access,
  settingsPlugin
} = {}) {
  const normalizedPolicy = getNormalizedSettingsPluginPolicy(settingsPlugin || DEFAULT_SETTINGS_PLUGIN_POLICY);
  const normalizedRole = String(role || 'user').trim().toLowerCase() === 'admin' ? 'admin' : 'user';
  const normalizedAuthMode = String(authMode || 'single').trim().toLowerCase() === 'multi' ? 'multi' : 'single';
  const hasReadAccess = Boolean(canRead);
  const canSeeAdminPanels = hasReadAccess
    && Boolean(canAdmin)
    && normalizedRole === 'admin'
    && normalizedAuthMode === 'multi';
  const configuredPanels = canSeeAdminPanels
    ? normalizedPolicy.visiblePanelsByRole.admin
    : [SETTINGS_APPEARANCE_PANEL_ID];
  const coreVisiblePanels = [];
  const corePanelSet = new Set();
  configuredPanels.forEach((panelId) => {
    const normalizedPanelId = String(panelId || '').trim().toLowerCase();
    if (!normalizedPanelId || corePanelSet.has(normalizedPanelId)) return;
    if (!canSeeAdminPanels && normalizedPanelId !== SETTINGS_APPEARANCE_PANEL_ID) return;
    corePanelSet.add(normalizedPanelId);
    coreVisiblePanels.push(normalizedPanelId);
  });
  if (!corePanelSet.has(SETTINGS_APPEARANCE_PANEL_ID)) {
    coreVisiblePanels.unshift(SETTINGS_APPEARANCE_PANEL_ID);
    corePanelSet.add(SETTINGS_APPEARANCE_PANEL_ID);
  }

  const normalizedContribution = normalizedPolicy.appContribution;
  const appPanels = filterContributionDescriptors(normalizedContribution?.panels, access);
  const appearanceSections = filterContributionDescriptors(normalizedContribution?.appearanceSections, access);
  const visiblePanels = [];
  coreVisiblePanels.forEach((panelId) => {
    visiblePanels.push(panelId);
    if (panelId === SETTINGS_APPEARANCE_PANEL_ID) {
      appPanels.forEach((descriptor) => visiblePanels.push(descriptor.id));
    }
  });
  const visiblePanelSet = new Set(visiblePanels);
  const appPanelById = new Map(appPanels.map((descriptor) => [descriptor.id, descriptor]));
  const appearanceScopePolicy = normalizedRole === 'admin'
    ? normalizedPolicy.appearanceScopePolicy.admin
    : normalizedPolicy.appearanceScopePolicy.default;
  const requiresAdminData = coreVisiblePanels.some((panelId) => SETTINGS_ADMIN_PANEL_IDS.includes(panelId));

  return {
    settingsPlugin: normalizedPolicy,
    visiblePanels,
    visiblePanelSet,
    coreVisiblePanels,
    appPanels,
    appPanelById,
    appearanceSections,
    bridgeCoreThemePreference: normalizedContribution?.bridgeCoreThemePreference === true
      && appearanceSections.length > 0,
    providerPluginId: appPanels.length > 0 || appearanceSections.length > 0
      ? String(normalizedContribution?.providerPluginId || '').trim()
      : '',
    initialPanel: coreVisiblePanels[0] || SETTINGS_APPEARANCE_PANEL_ID,
    canSeeAdminPanels,
    requiresAdminData,
    isAppearanceOnly: visiblePanels.length === 1 && visiblePanels[0] === SETTINGS_APPEARANCE_PANEL_ID,
    appearanceScopePolicy
  };
}
